import { Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TppZipConnection } from '../../../services/transitPassPool.service';
import { formatTppLocalDateTime } from './tppUiShared';

type Props = {
  zipConnected: boolean;
  zipConn: TppZipConnection | null;
  showGuestChip: boolean;
  onSwitchZip: () => void;
  onDownloadZip?: () => void;
  downloadPending?: boolean;
};

/** ページ見出し横の Zip 接続状態・切替・未DL時の明示ダウンロード */
export default function TppZipSessionBadge({
  zipConnected,
  zipConn,
  showGuestChip,
  onSwitchZip,
  onDownloadZip,
  downloadPending = false,
}: Props) {
  const { t, i18n } = useTranslation();

  const guestChip = showGuestChip ? (
    <Badge bg="success">{t('tpp.guest_mode_chip')}</Badge>
  ) : null;

  if (!zipConnected) {
    if (!guestChip) return null;
    return <div className="d-flex flex-wrap justify-content-end gap-1">{guestChip}</div>;
  }
  if (!zipConn) return null;

  const manual = zipConn.autoSaveMode === 'manual-download';
  const dirty = manual && zipConn.dirty;
  const modeHint = manual ? t('tpp.zip_autosave_manual') : t('tpp.zip_autosave_file');
  const savedLabel = formatTppLocalDateTime(zipConn.lastSavedAt, i18n.language);
  // 未DL はバッジだけ。メタ行に重ねない
  const metaLine = [
    zipConn.fileName,
    savedLabel ? `${t('tpp.zip_saved_at_short')} ${savedLabel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="text-end small">
      <div className="d-flex flex-wrap justify-content-end align-items-center gap-1 mb-1">
        {guestChip}
        {dirty ? (
          <Badge bg="warning" text="dark" title={modeHint}>
            {t('tpp.zip_dirty_short')}
          </Badge>
        ) : (
          <Badge bg="success" title={modeHint}>
            {t('tpp.zip_connected')}
          </Badge>
        )}
      </div>
      {metaLine ? (
        <div className="text-muted text-break mb-1" title={modeHint}>
          {metaLine}
        </div>
      ) : null}
      <div className="d-flex flex-wrap justify-content-end gap-2 align-items-center">
        {dirty && onDownloadZip ? (
          <Button
            type="button"
            variant="outline-warning"
            size="sm"
            className="py-0"
            disabled={downloadPending}
            onClick={onDownloadZip}
            title={modeHint}
          >
            {t('tpp.zip_download_now')}
          </Button>
        ) : null}
        <Button variant="link" size="sm" className="p-0" onClick={onSwitchZip}>
          {t('tpp.zip_switch')}
        </Button>
      </div>
    </div>
  );
}
